import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  List,
  Tag,
  Typography,
  Space,
  Empty,
  Skeleton,
  Tooltip,
  Input,
  Select,
  Form,
  message,
} from "antd";
import {
  InboxOutlined,
  DeleteOutlined,
  FileTextOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { Button, Modal } from "../../../components/Common";
import { useMutations } from "../../../hooks/useApiMutation";
import { useConfirm } from "../../../hooks/useConfirm";

const { Text } = Typography;

const PURPOSE_TAG = {
  knowledge: "blue",
  image: "cyan",
  code: "geekblue",
  other: "default",
};

const formatSize = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const FilesTab = ({ agentId, onChanged }) => {
  const { mutate } = useMutations();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;
  const { confirmAction } = useConfirm();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await mutateRef.current("fetchAgentFiles", {
        method: "get",
        endpoint: `/v1/agents/${agentId}/files`,
      });
      setFiles(data?.data || []);
    } catch {
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    load();
  }, [load]);

  const onAttach = async () => {
    try {
      const values = await form.validateFields();
      await mutate("addAgentFile", {
        method: "post",
        endpoint: `/v1/agents/${agentId}/files`,
        payload: values,
        alert: { success: "File attached." },
      });
      setOpen(false);
      form.resetFields();
      load();
      onChanged?.();
    } catch (err) {
      if (err?.errorFields) return;
      message.error("Failed to attach file");
    }
  };

  const renderItem = (f) => (
    <List.Item
      actions={[
        f.openAiFileId && (
          <Tooltip title={`OpenAI File ID: ${f.openAiFileId}`}>
            <Tag icon={<LinkOutlined />} color="green">
              OpenAI
            </Tag>
          </Tooltip>
        ),
        f.claudeFileId && (
          <Tooltip title={`Claude File ID: ${f.claudeFileId}`}>
            <Tag icon={<LinkOutlined />} color="purple">
              Claude
            </Tag>
          </Tooltip>
        ),
        <Button
          variant="ghost"
          danger
          icon={<DeleteOutlined />}
          aria-label={`Remove ${f.originalName}`}
          onClick={() =>
            confirmAction({
              key: "deleteAgentFile",
              method: "delete",
              endpoint: `/v1/agents/${agentId}/files/${f.agentFileId}`,
              title: "Remove file",
              content: `Remove "${f.originalName}" from this agent?`,
              okText: "Remove",
              okType: "danger",
              onSuccess: () => {
                load();
                onChanged?.();
              },
            })
          }
        />,
      ].filter(Boolean)}
    >
      <List.Item.Meta
        avatar={
          <FileTextOutlined
            style={{ fontSize: 24, color: "var(--accent-blue)" }}
          />
        }
        title={
          <Space>
            <Text strong>{f.originalName}</Text>
            <Tag color={PURPOSE_TAG[f.purpose] || "default"}>{f.purpose}</Tag>
            <Tag>{f.status}</Tag>
          </Space>
        }
        description={
          <Space split="·" size="small">
            <Text type="secondary">{f.mimeType || "unknown"}</Text>
            <Text type="secondary">{formatSize(f.sizeBytes)}</Text>
            <Text type="secondary">
              {new Date(f.createdAt).toLocaleDateString()}
            </Text>
          </Space>
        }
      />
    </List.Item>
  );

  return (
    <div>
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div>
          <Text strong style={{ fontSize: 16 }}>
            Knowledge base
          </Text>
          <div>
            <Text type="secondary">
              Files attached to the agent as persistent context. Ephemeral
              per-call attachments live separately.
            </Text>
          </div>
        </div>
        <Button
          variant="primary"
          icon={<InboxOutlined />}
          onClick={() => setOpen(true)}
        >
          Attach file reference
        </Button>
      </Space>

      {loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : files.length === 0 ? (
        <Empty description="No files attached yet" />
      ) : (
        <List itemLayout="horizontal" dataSource={files} renderItem={renderItem} />
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={onAttach}
        title="Attach file reference"
        confirmText="Attach"
        width={640}
      >
        <Text type="secondary">
          Upload the file to OpenAI / Claude first, then register it here with
          its remote id. A real upload widget will replace this once the
          storage pipeline is wired up.
        </Text>
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="originalName"
            label="File name"
            rules={[{ required: true }]}
          >
            <Input placeholder="contract_template.pdf" />
          </Form.Item>
          <Form.Item name="mimeType" label="MIME type">
            <Input placeholder="application/pdf" />
          </Form.Item>
          <Form.Item name="purpose" label="Purpose" initialValue="knowledge">
            <Select>
              <Select.Option value="knowledge">Knowledge</Select.Option>
              <Select.Option value="image">Image</Select.Option>
              <Select.Option value="code">Code</Select.Option>
              <Select.Option value="other">Other</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="sizeBytes" label="Size (bytes)" initialValue={0}>
            <Input type="number" />
          </Form.Item>
          <Form.Item name="openAiFileId" label="OpenAI File ID">
            <Input placeholder="file-..." />
          </Form.Item>
          <Form.Item name="openAiVectorId" label="OpenAI Vector Store ID">
            <Input placeholder="vs_..." />
          </Form.Item>
          <Form.Item name="claudeFileId" label="Claude File ID">
            <Input placeholder="..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FilesTab;
